import { ColumnDef, RowData } from "@tanstack/react-table";

/**
 * A column definition of any value type, for arrays that mix columns. TanStack
 * documents `ColumnDef<TData, any>` for this: `TValue` is both an input and an
 * output of a column, so `unknown` rejects columns built with `accessor`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ColumnDef is invariant in TValue; see the comment above.
export type AnyColumnDef<TData extends RowData> = ColumnDef<TData, any>;

export interface ColumnMeta<
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- type parameters must match TanStack's ColumnMeta for declaration merging
	TData extends RowData,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- type parameters must match TanStack's ColumnMeta for declaration merging
	TValue,
	TSortField extends string,
> {
	/**
	 * Specifies the alignment of the cell in the column.
	 * @default "left"
	 */
	align?: "left" | "center" | "right";
	verticalAlign?: "top" | "center";
	/**
	 * Sets the field to sort by when sorting this column. If not specified, the column will not be sortable.
	 */
	sortField?: TSortField;
	/**
	 * Makes the column sortable, using the column ID as the sort field.
	 * @default false
	 */
	sortable?: boolean;
	/**
	 * Truncates the text in cells in this column.
	 * @default false
	 */
	truncate?: boolean;
}

export type PageInfo = {
	hasNextPage: boolean;
	totalCount: number;
	endCursor: string;
	startCursor: string;
};

export enum DataTableSortDirection {
	Asc = "ASC",
	Desc = "DESC",
}

export type DataTableSortState<
	TSortField extends string = string,
	TSortDirection extends "ASC" | "DESC" = DataTableSortDirection,
> = {
	field: TSortField;
	direction: TSortDirection;
};
